import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useColorModeValue,
  IconButton,
  Collapse,
  useDisclosure,
  Heading
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

// RangeInputコンポーネント
function RangeInput({ label, minValue, maxValue, onMinChange, onMaxChange, unit }) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <HStack spacing={2}>
        <Input
          type="number"
          value={minValue ?? ''}
          onChange={(e) => onMinChange(e.target.value)}
          placeholder={`最小${unit || ''}`}
        />
        <Box>～</Box>
        <Input
          type="number"
          value={maxValue ?? ''}
          onChange={(e) => onMaxChange(e.target.value)}
          placeholder={`最大${unit || ''}`}
        />
      </HStack>
    </FormControl>
  );
}

export default function EquipmentFilter({
  config,
  filterState,
  onFilterChange,
  manufacturers,
  selectedManufacturer,
  onManufacturerChange,
  items = [],
  isTsubo = false,
  sortState = {},
  onSortChange = () => {},
}) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleFilterChange = (field, value) => {
    onFilterChange({ ...filterState, [field]: value });
  };

  // string型でメーカー以外のフィールドを抽出
  const stringFields = (config.fields || []).filter(
    field => field.type === 'string' && field.name !== 'manufacturer'
  );
  // boolean型フィールド
  const booleanFields = (config.fields || []).filter(field => field.type === 'boolean');
  // number型フィールド
  const numberFields = (config.fields || []).filter(field => field.type === 'number');

  // 各string型フィールドのユニーク値リスト
  const stringFieldOptions = {};
  stringFields.forEach(field => {
    stringFieldOptions[field.name] = Array.from(
      new Set(items.map(item => item[field.name]).filter(v => v && v !== ''))
    );
  });

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
      <Button
        onClick={onToggle}
        variant="ghost"
        width="100%"
        mb={isOpen ? 4 : 0}
        rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      >
        絞り込み・並び替え
      </Button>

      <Collapse in={isOpen}>
      <Heading size="md" p={2} mb={2}>絞り込み</Heading>
        <VStack spacing={4} align="stretch">
          {/* 共通フィルター */}
          <HStack spacing={4}>
            <Checkbox
              isChecked={filterState.showOnlyLiked}
              onChange={(e) => handleFilterChange('showOnlyLiked', e.target.checked)}
            >
              お気に入り
            </Checkbox>
            <Checkbox
              isChecked={filterState.showOnlyUserEquipment}
              onChange={(e) => handleFilterChange('showOnlyUserEquipment', e.target.checked)}
            >
              お客様にて登録した{config.key==='floorplan' ? '間取り' : '設備'}
            </Checkbox>
            {/* boolean型フィールドのチェックボックス */}
            {booleanFields.map(field => (
              <Checkbox
                key={field.name}
                isChecked={!!filterState[field.name]}
                onChange={(e) => handleFilterChange(field.name, e.target.checked)}
              >
                {field.label}
              </Checkbox>
            ))}
          </HStack>

          {/* メーカー */}
          <FormControl>
            <FormLabel>メーカー</FormLabel>
            <Select
              value={selectedManufacturer}
              onChange={(e) => onManufacturerChange(e.target.value)}
            >
              <option value="all">すべて</option>
              {manufacturers.map(manufacturer => (
                <option key={manufacturer} value={manufacturer}>
                  {manufacturer}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* string型フィールドのプルダウン */}
          {stringFields.map(field => (
            <FormControl key={field.name}>
              <FormLabel>{field.label}</FormLabel>
              <Select
                value={filterState[field.name] || ''}
                onChange={(e) => handleFilterChange(field.name, e.target.value)}
              >
                <option value="">すべて</option>
                {stringFieldOptions[field.name].map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>
            </FormControl>
          ))}

          <FormControl>
            <FormLabel>検索</FormLabel>
            <Input
              value={filterState.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="名称・特徴で検索"
            />
          </FormControl>

         {/* 設備特有のフィルター */}
          {config.fields?.map(field => {
            if (field.type === 'number') {
              const minKey = `min_${field.name}`;
              const maxKey = `max_${field.name}`;
              // floorplanのareaフィールドのみ単位切替
              if (config.key === 'floorplan' && field.unit === 'm2') {
                return (
                  <RangeInput
                    key={field.name}
                    label={field.label + (isTsubo ? '（坪）' : '（m²）')}
                    minValue={filterState[minKey]}
                    maxValue={filterState[maxKey]}
                    onMinChange={value => {
                      handleFilterChange(minKey, value);
                    }}
                    onMaxChange={value => {
                      handleFilterChange(maxKey, value);
                    }}
                    unit={isTsubo ? '坪' : 'm²'}
                  />
                );
              }
              // 通常の数値フィルター
              return (
                <RangeInput
                  key={field.name}
                  label={field.label + (field.unit ? `（${field.unit}）` : '')}
                  minValue={filterState[minKey]}
                  maxValue={filterState[maxKey]}
                  onMinChange={(value) => handleFilterChange(minKey, value)}
                  onMaxChange={(value) => handleFilterChange(maxKey, value)}
                  unit={field.unit}
                />
              );
            }
            if (field.type === 'select') {
              return (
                <FormControl key={field.name}>
                  <FormLabel>{field.label}</FormLabel>
                  <Select
                    value={filterState[field.name] || ''}
                    onChange={(e) => handleFilterChange(field.name, e.target.value)}
                  >
                    <option value="">すべて</option>
                    {field.options?.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              );
            }
            return null;
          })}
          {/* ソート機能 */}
          {numberFields.length > 0 && (
            <FormControl>
              <Heading size="md" p={2} mb={2}>並び替え</Heading>
              <HStack spacing={2} wrap="wrap">
                <Box>
                  <FormLabel fontSize="md" mb={0}>何で並び替えますか？</FormLabel>
                  <Select
                    size="md"
                    value={sortState.key || ''}
                    onChange={e => onSortChange({ ...sortState, key: e.target.value })}
                  >
                    <option value="">選択しない</option>
                    {numberFields.map(field => (
                      <option key={field.name} value={field.name}>{field.label}</option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <FormLabel fontSize="md" mb={0}>順番</FormLabel>
                  <Select
                    size="md"
                    value={sortState.order || ''}
                    onChange={e => onSortChange({ ...sortState, order: e.target.value })}
                    isDisabled={!sortState.key}
                  >
                    <option value="asc">昇順</option>
                    <option value="desc">降順</option>
                  </Select>
                </Box>
              </HStack>
            </FormControl>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
} 